import React from 'react';
import { useAuth } from '../hooks/useAuth';

export interface LogoutButtonProps {
  /**
   * Button text
   * @default 'Logout'
   */
  children?: React.ReactNode;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Button variant
   * @default 'secondary'
   */
  variant?: 'primary' | 'secondary' | 'outline';

  /**
   * Button size
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Show logout icon
   * @default true
   */
  showIcon?: boolean;

  /**
   * Callback before logout starts
   */
  onLogoutStart?: () => void;

  /**
   * Custom inline styles
   */
  style?: React.CSSProperties;

  /**
   * Disable the button
   */
  disabled?: boolean;
}

/**
 * Pre-built logout button component
 *
 * Triggers the Keycloak logout flow when clicked. The button automatically
 * hides when the user is not authenticated.
 *
 * @example Basic usage
 * ```tsx
 * import { LogoutButton } from 'antaeus.keycloak.react';
 *
 * function Header() {
 *   return <LogoutButton />;
 * }
 * ```
 *
 * @example Customized
 * ```tsx
 * <LogoutButton
 *   variant="outline"
 *   size="small"
 *   onLogoutStart={() => {
 *     // Clean up app state
 *     localStorage.clear();
 *   }}
 * >
 *   Sign Out
 * </LogoutButton>
 * ```
 *
 * @example With custom styling
 * ```tsx
 * <LogoutButton
 *   className="my-logout-btn"
 *   style={{ backgroundColor: '#dc3545' }}
 * />
 * ```
 */
export const LogoutButton: React.FC<LogoutButtonProps> = ({
  children = 'Logout',
  className = '',
  variant = 'secondary',
  size = 'medium',
  showIcon = true,
  onLogoutStart,
  style,
  disabled = false,
}) => {
  const auth = useAuth();

  // Don't render if not authenticated
  if (!auth.isAuthenticated) {
    return null;
  }

  const handleClick = () => {
    onLogoutStart?.();
    auth.signoutRedirect();
  };

  const baseClass = 'kc-button kc-logout-button';
  const variantClass = `kc-button--${variant}`;
  const sizeClass = `kc-button--${size}`;
  const classes = `${baseClass} ${variantClass} ${sizeClass} ${className}`.trim();

  return (
    <button
      type="button"
      className={classes}
      onClick={handleClick}
      disabled={disabled || auth.isLoading}
      style={style}
    >
      {showIcon && <span className="kc-button__icon">🚪</span>}
      <span className="kc-button__text">{children}</span>
    </button>
  );
};
